import { useEffect } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import isObject from 'lodash/isObject'
import { ObjectDataRecord } from 'src/types/api/objects.api'
import { FormInput } from '@microservices/wiskey-react-components'
import { GridRenderCellParams } from '@microservices/wiskey-react-table-component'
import { Clear as ClearIcon } from '@mui/icons-material'
import { IconButton } from '@mui/material'

import { EditCellObjectPicker } from '@pages/ConfiguredEntity/components/EditCellObjectPicker'
import { useDefaultViewEditValues } from '@pages/ConfiguredEntity/components/EditCellRender/hooks/useDefaultViewEditValues'
import { unsavedChangesRefType } from '@pages/ConfiguredEntity/types'

import { EnumAutocompleteController } from '@components/hookFormControllers/EnumAutocompleteController'
import { MappedCheckboxForForm } from '@components/MappedCheckbox'
import { SelectBoolean } from '@components/SelectBoolean'

import { getInputTypeByValueType } from '@helpers'
import {
  FIELD_VALUE_FORMAT,
  FIELD_VALUE_TYPE,
  GRID_CELL_MODE,
  HOT_KEY,
  MAX_INPUT_LENGTH,
  OBJECT_FIELD_TYPE,
} from '@constants'
import { AutocompleteOption, GETField } from '@types'

export type PayloadRecord = { [key: string]: string; _id: string }

type EditCellRenderProps = {
  cellObjectFieldModel: string | undefined
  params: GridRenderCellParams<unknown, ObjectDataRecord, unknown>
  updateViewCell: (payload: {
    viewId: string
    record: PayloadRecord
    contextId?: string
  }) => Promise<void>
  isLoading: boolean
  isEditMode: boolean
  hasNewRows: boolean
  hasWrapRowText: boolean
  contextId?: string
  viewId: string
  formId: string
  cellField: GETField | undefined
  setDropDownWindowOpen: (value: boolean) => void
  unsavedChangesRef: unsavedChangesRefType
}

export const EditCellRender = ({
  cellObjectFieldModel,
  params,
  isLoading,
  isEditMode,
  hasNewRows,
  cellField,
  setDropDownWindowOpen,
  unsavedChangesRef,
  hasWrapRowText,
}: EditCellRenderProps) => {
  const methods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  const objectPickerValue = useDefaultViewEditValues(params, cellField, unsavedChangesRef)

  useEffect(() => {
    const handleScroll = () => {
      const isCellInEditMode =
        params.api.getCellMode(params.id, params.field) === GRID_CELL_MODE.EDIT
      if (isCellInEditMode) {
        params.api.stopCellEditMode({ id: params.id, field: params.field })
      }
    }

    const events = ['scrollPositionChange', 'columnResizeStart'] as const
    const unsubscribes = events.map(event => params.api.subscribeEvent(event, handleScroll))

    return () => {
      unsubscribes.forEach(unsub => unsub())
    }
  }, [params.api, params.field, params.id])

  if (!cellField) {
    return
  }

  const handleUpdateViewCell = async value => {
    if (
      cellField?.valueType === FIELD_VALUE_TYPE.INTEGER &&
      cellField?.type !== OBJECT_FIELD_TYPE.ENUM &&
      !isObject(value)
    ) {
      value = value ? Number(value) : 0
    }

    if (cellField?.type === OBJECT_FIELD_TYPE.ENUM) {
      value = value ? value?.id : value
    }

    params.api.setEditCellValue({
      id: params.id,
      field: params.field,
      value,
    })
  }

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLDivElement>,
    value: AutocompleteOption | string | null
  ) => {
    e.stopPropagation()
    if (e.key === HOT_KEY.ENTER || e.key === HOT_KEY.TAB) {
      handleUpdateViewCell(value)
      params.api.stopCellEditMode({ id: params.id, field: params.field })
    }

    if (e.key === HOT_KEY.ESCAPE) {
      params.api.stopCellEditMode({ id: params.id, field: params.field, ignoreModifications: true })
    }
  }

  const getType = (valueType: string) => {
    if (valueType === FIELD_VALUE_TYPE.INTEGER) return 'number'

    return 'string'
  }

  const defaultCellInputSx = {
    backgroundColor: '#fff',
    padding: 0,
    height: '23px',
    '& .MuiOutlinedInput-root': { padding: 0, height: '23px' },
    '& .MuiInputBase-input': { padding: 0, height: '23px' },
    '& .MuiInputBase-root': { padding: '0 10px', height: '23px' },
    '& .MuiOutlinedInput-input': { padding: 0, height: '23px' },
  }

  const asCheckbox =
    cellField?.valueFormat === FIELD_VALUE_FORMAT.NUMBER ||
    cellField?.valueFormat === FIELD_VALUE_FORMAT.BOOLEAN

  const renderCellInput = () => {
    if (
      cellField &&
      (cellField?.valueType === FIELD_VALUE_TYPE.OBJ_PK_LINK ||
        cellField?.valueType === FIELD_VALUE_TYPE.OBJ_INTERNAL_ID_LINK)
    ) {
      return (
        <Controller
          key={`${params.id}-${params.field}`}
          control={methods.control}
          defaultValue={objectPickerValue}
          name={`${params.id}-${params.field}`}
          render={({ field: { onChange, value }, fieldState: { invalid } }) => {
            return (
              <EditCellObjectPicker
                cellField={cellField}
                cellObjectFieldModel={cellObjectFieldModel}
                handleUpdateViewCell={handleUpdateViewCell}
                hasWrapRowText={hasWrapRowText}
                invalid={invalid}
                isLoading={isLoading}
                isSingleEditMode={!isEditMode && !hasNewRows}
                params={params}
                setDropDownWindowOpen={setDropDownWindowOpen}
                value={value ?? objectPickerValue}
                onChange={onChange}
              />
            )
          }}
          rules={{
            required: cellField.userRequired ?? false,
          }}
        />
      )
    }

    if (asCheckbox && cellField?.valueFormat) {
      return (
        <MappedCheckboxForForm
          formRelatedProps={{
            label: '',
            field: cellField.code,
            name: cellField.code,
          }}
          mappedCheckboxProps={{
            valueFormat: cellField.valueFormat,
            disabled: false,
            outerValue: params.value,
            setOuterValue: handleUpdateViewCell,
            checkboxSx: {
              top: 0,
              padding: 0,
              height: '23px',
              width: '23px',
            },
          }}
        />
      )
    }

    if (cellField?.valueType === FIELD_VALUE_TYPE.BOOLEAN) {
      return (
        <Controller
          control={methods.control}
          defaultValue={params.value ?? ''}
          name={`${params.id}-${params.field}`}
          render={({ field: { onChange, value } }) => (
            <SelectBoolean
              autoFocus
              disabled={isLoading}
              dotsIcon={false}
              isDialog={true}
              sx={defaultCellInputSx}
              value={params.value == null ? '' : String(params.value)}
              onChange={e => {
                const newValue = e.target.value
                onChange(newValue)
                handleUpdateViewCell(newValue)
                params.api.stopCellEditMode({ id: params.id, field: params.field })
              }}
            />
          )}
        />
      )
    }

    if (cellField?.type === OBJECT_FIELD_TYPE.ENUM) {
      return (
        <EnumAutocompleteController
          autoFocus
          enumDescription={cellField?.enumDescription}
          fieldCode={cellField?.value}
          handleKeyDown={params.value || isEditMode || hasNewRows ? handleKeyDown : undefined}
          inputSx={defaultCellInputSx}
          name={cellField?.code}
          objectCode={cellField?.objectCode ?? ''}
          value={params.value}
          valueType={cellField?.valueType as FIELD_VALUE_TYPE}
          rules={{
            required: cellField.userRequired ?? false,
          }}
          onChange={handleUpdateViewCell}
        />
      )
    }

    return (
      <Controller
        control={methods.control}
        defaultValue={params.value}
        name={`${params.id}-${params.field}`}
        render={({ field: { onChange, value }, fieldState: { error } }) => {
          const inputValue =
            value && cellField?.valueType === FIELD_VALUE_TYPE.INTEGER ? Number(value) : value

          return (
            <FormInput
              key={cellField?.code}
              applyDateByWholeNumber
              autoFocus
              popoverDateShiftMode
              formInputContainerSx={{ m: 0 }}
              formatDate={cellField?.formatDate || undefined}
              inputType={getInputTypeByValueType(cellField?.valueType)}
              maxLengthInput={MAX_INPUT_LENGTH}
              name={`${params.id}-${params.field}`}
              size='small'
              sx={{ ...defaultCellInputSx, backgroundColor: error && '#ffcfd6' }}
              type={getType(cellField?.valueType)}
              value={inputValue}
              errorIconSxForDate={{
                top: 1,
              }}
              textFieldInputProps={{
                endAdornment: value && !cellField?.userRequired && (
                  <IconButton size='small' sx={{ width: 24 }} onClick={() => onChange('')}>
                    <ClearIcon fontSize='small' />
                  </IconButton>
                ),
              }}
              onBlurInput={e => (!error || isEditMode || hasNewRows) && handleUpdateViewCell(value)}
              onChange={onChange}
              onKeyDown={e => (!error || isEditMode || hasNewRows) && handleKeyDown(e, value)}
            />
          )
        }}
        rules={{
          required: cellField.userRequired ?? false,
          validate: value => {
            if (cellField?.valueType === FIELD_VALUE_TYPE.INTEGER) {
              const num = Number(value)

              return Number.isInteger(num)
            }
          },
        }}
      />
    )
  }

  return <FormProvider {...methods}>{renderCellInput()}</FormProvider>
}
